import React, { useState } from 'react'
import TypeButton from '../TypeButton'
import {
  ALL_BUTTONS_TYPES,
  BUTTONS_TYPES,
  LINK_BUTTON,
  PHONE_CALL_BUTTON,
  REPLY_BUTTON,
  BUTTON_ERROR_TYPES as BUTTON_ERRORS,
  BUTTON_ERRORS_MESSAGES
} from '../constants'
import {
  EnvelopeIcon,
  CalendarIcon,
  LinkIcon,
  CheckIcon
} from '@heroicons/react/24/outline'

const AVAILABLE_ACTIONS = [
  {
    id: 'send-email',
    name: 'Send Email',
    description: 'Triggers email sending with configured template',
    icon: EnvelopeIcon,
    color: 'green',
    fields: ['emailIndex']
  },
  {
    id: 'schedule-flow',
    name: 'Schedule Flow',
    description: 'Schedule a flow for tomorrow',
    icon: CalendarIcon,
    color: 'blue',
    fields: ['flowName']
  },
  {
    id: 'webhook',
    name: 'Webhook',
    description: 'Trigger external webhook',
    icon: LinkIcon,
    color: 'purple',
    fields: ['webhookUrl']
  }
]

const getActionColorClasses = (color, selected) => {
  const colorMap = {
    green: selected
      ? 'border-green-500 bg-green-50 ring-1 ring-green-500'
      : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50/50',
    blue: selected
      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
      : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50',
    purple: selected
      ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
      : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/50'
  }
  return colorMap[color] || colorMap.green
}

const ButtonForm = ({
  buttonInfo = {},
  setOpen,
  onClick,
  onCancel,
  isConversational = false,
  replyOptions = [],
  emailFields = []
}) => {
  const [type, setType] = useState(buttonInfo.type || LINK_BUTTON)
  const [buttonText, setButtonText] = useState(buttonInfo.text || '')
  const [phone, setPhone] = useState(buttonInfo.phone || '')
  const [link, setLink] = useState(buttonInfo.link || '')
  const [replyFlow, setReplyFlow] = useState(buttonInfo.replyFlow || '')
  const [errors, setEerrors] = useState([])
  const hasSendEmailAction = buttonInfo.sendEmail || buttonInfo.action === 'send-email'
  const [sendEmail, setSendEmail] = useState(hasSendEmailAction)
  const [sendEmailIndex, setSendEmailIndex] = useState(buttonInfo.sendEmailIndex || '1')
  const [customEmail, setCustomEmail] = useState(buttonInfo.customEmail || '')
  const [selectedAction, setSelectedAction] = useState(hasSendEmailAction ? 'send-email' : null)

  const handleTypeChange = (type) => {
    setType(type)
  }

  const handleButtonTextChange = (event) => {
    setButtonText(event.target.value)
  }

  const handlePhoneChange = (event) => {
    setPhone(event.target.value)
  }

  const handleLinkChange = (event) => {
    setLink(event.target.value)
  }

  const isValid = (buttonInfo) => {
    const newErrors = []
    if (!buttonInfo.text) newErrors.push(BUTTON_ERRORS.EMPTY_DISPLAY_TEXT)
    if (buttonInfo.type === LINK_BUTTON && !buttonInfo.link)
      newErrors.push(BUTTON_ERRORS.EMPTY_LINK)
    if (buttonInfo.type === PHONE_CALL_BUTTON && !buttonInfo.phone)
      newErrors.push(BUTTON_ERRORS.EMPTY_PHONE)
    if (buttonInfo.type === REPLY_BUTTON && !buttonInfo.replyFlow)
      newErrors.push(BUTTON_ERRORS.MISSING_REPLY)
    setEerrors(newErrors)
    if (newErrors.length > 0) return false
    return true
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    const button = {
      type,
      text: buttonText
    }
    if (type === LINK_BUTTON) button.link = link
    if (type === PHONE_CALL_BUTTON) button.phone = phone
    if (type === REPLY_BUTTON) button.replyFlow = replyFlow

    // Handle actions
    if (selectedAction === 'send-email' && sendEmail) {
      button.sendEmail = true
      button.sendEmailIndex = sendEmailIndex
      button.action = 'send-email'
      if (customEmail && customEmail.trim()) {
        button.customEmail = customEmail.trim()
      }
    } else if (selectedAction) {
      button.action = selectedAction
    }

    if (isValid(button)) onClick(button)
  }

  const getButtonsSelect = () => {
    const buttonsTypes = !isConversational ? BUTTONS_TYPES : ALL_BUTTONS_TYPES
    return buttonsTypes.map((buttonType) => (
      <TypeButton
        key={buttonType.type}
        type={buttonType.type}
        selected={type === buttonType.type}
        onClick={handleTypeChange}
        icon={buttonType.icon}
      >
        {buttonType.name}
      </TypeButton>
    ))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">
          {buttonInfo.type ? 'Editar Botón' : 'Crear Botón'}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {buttonInfo.type
            ? 'Modifica la configuración del botón'
            : 'Configura un nuevo botón o sugerencia'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo</label>
        <div className="flex gap-2 flex-wrap">{getButtonsSelect()}</div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Texto del botón</label>
        <p className="text-xs text-gray-400 mb-2">Máximo 25 caracteres</p>
        <input
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-colors"
          type="text"
          value={buttonText}
          onChange={handleButtonTextChange}
          maxLength={25}
          placeholder="Ej: Ver más"
        />
        {errors.includes(BUTTON_ERRORS.EMPTY_DISPLAY_TEXT) && (
          <p className="text-red-500 text-xs mt-1.5">
            {BUTTON_ERRORS_MESSAGES[BUTTON_ERRORS.EMPTY_DISPLAY_TEXT]}
          </p>
        )}
      </div>

      {/* Actions */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          Acciones al pulsar (opcional)
        </label>
        <p className="text-xs text-gray-400 mb-3">Selecciona qué ocurre cuando el usuario pulsa este botón</p>

        <div className="grid grid-cols-1 gap-2.5">
          {AVAILABLE_ACTIONS.map((action) => {
            const Icon = action.icon
            const isSelected = selectedAction === action.id
            return (
              <div
                key={action.id}
                onClick={() => {
                  if (selectedAction === action.id) {
                    setSelectedAction(null)
                    if (action.id === 'send-email') setSendEmail(false)
                  } else {
                    setSelectedAction(action.id)
                    if (action.id === 'send-email') setSendEmail(true)
                  }
                }}
                className={`p-3.5 rounded-xl border-2 cursor-pointer transition-all ${getActionColorClasses(
                  action.color,
                  isSelected
                )}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-white' : 'bg-gray-100'}`}>
                    <Icon className={`h-5 w-5 ${isSelected ? `text-${action.color}-600` : 'text-gray-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-800 text-sm">{action.name}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{action.description}</p>
                  </div>
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                      isSelected
                        ? `border-${action.color}-500 bg-${action.color}-500`
                        : 'border-gray-300'
                    }`}
                  >
                    {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                  </div>
                </div>

                {isSelected && action.id === 'send-email' && (
                  <div
                    className="mt-4 ml-11 pt-4 border-t border-green-200 space-y-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Plantilla de email
                      </label>
                      {emailFields.length > 0 ? (
                        <select
                          value={sendEmailIndex}
                          onChange={(e) => setSendEmailIndex(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                        >
                          {emailFields.map((field, idx) => (
                            <option key={idx} value={String(idx + 1)}>
                              {idx + 1} — {field.subject || `Email ${idx + 1}`}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={sendEmailIndex}
                            onChange={(e) => setSendEmailIndex(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                          <p className="text-xs text-orange-500 mt-1.5">
                            No hay plantillas configuradas en esta template
                          </p>
                        </>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Email destino (opcional)
                      </label>
                      <input
                        type="email"
                        placeholder="ej: equipo@empresa.com"
                        value={customEmail}
                        onChange={(e) => setCustomEmail(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Si lo dejas vacío, usa el email por defecto de la campaña
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {type === LINK_BUTTON && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Link URL</label>
          <input
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-colors"
            type="text"
            value={link}
            onChange={handleLinkChange}
            placeholder="https://..."
          />
          {errors.includes(BUTTON_ERRORS.EMPTY_LINK) && (
            <p className="text-red-500 text-xs mt-1.5">
              {BUTTON_ERRORS_MESSAGES[BUTTON_ERRORS.EMPTY_LINK]}
            </p>
          )}
        </div>
      )}

      {type === PHONE_CALL_BUTTON && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Número de teléfono</label>
          <input
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-colors"
            type="text"
            value={phone}
            onChange={handlePhoneChange}
            placeholder="+34 600 000 000"
          />
          {errors.includes(BUTTON_ERRORS.EMPTY_PHONE) && (
            <p className="text-red-500 text-xs mt-1.5">
              {BUTTON_ERRORS_MESSAGES[BUTTON_ERRORS.EMPTY_PHONE]}
            </p>
          )}
        </div>
      )}

      {type === REPLY_BUTTON && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Flujo de respuesta</label>
          <select
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white transition-colors"
            value={replyFlow}
            onChange={(e) => setReplyFlow(e.target.value)}
          >
            <option value="">Selecciona una respuesta</option>
            {replyOptions.map((reply) => (
              <option key={reply.value} value={reply.value}>
                {reply.label}
              </option>
            ))}
          </select>
          {errors.includes(BUTTON_ERRORS.MISSING_REPLY) && (
            <p className="text-red-500 text-xs mt-1.5">
              {BUTTON_ERRORS_MESSAGES[BUTTON_ERRORS.MISSING_REPLY]}
            </p>
          )}
        </div>
      )}

      <div className="w-full flex justify-between items-center pt-4 border-t border-gray-100">
        <button
          onClick={onCancel}
          type="button"
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancelar
        </button>
        <button
          className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
          type="submit"
        >
          {buttonInfo.type ? 'Actualizar' : 'Crear'}
        </button>
      </div>
    </form>
  )
}

export default ButtonForm
